import ExcelJS from 'exceljs';
import { VENTAS_PAYMENT_METHODS } from './ventasSalesConstants';

const HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFCFFAFE' },
};

export const MAX_BULK_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export const BULK_COL = {
  code: 'codigo',
  name: 'nombre',
  price: 'precio_pos',
  cant_efectivo: 'cant_efectivo',
  cant_transferencia: 'cant_transferencia',
  cant_yape: 'cant_yape',
  cant_plin: 'cant_plin',
  cant_otro: 'cant_otro',
};

const QTY_COLS = [
  'cant_efectivo',
  'cant_transferencia',
  'cant_yape',
  'cant_plin',
  'cant_otro',
];

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function cellText(cell) {
  if (cell == null) return '';
  const v = cell.value;
  if (v == null) return '';
  if (typeof v === 'object' && v.text != null) return String(v.text).trim();
  if (typeof v === 'object' && v.result != null) return String(v.result).trim();
  return String(v).trim();
}

function headerMapFromRow(row) {
  const map = {};
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const t = cellText(cell).toLowerCase();
    if (t) map[t] = colNumber;
  });
  return map;
}

function findHeaderRow(sheet) {
  const required = [BULK_COL.code, BULK_COL.cant_efectivo];
  const max = Math.min(30, sheet.rowCount || 30);
  for (let r = 1; r <= max; r++) {
    const row = sheet.getRow(r);
    const m = headerMapFromRow(row);
    if (required.every((c) => m[c])) {
      return { rowIndex: r, colMap: m };
    }
  }
  return null;
}

function parseQty(raw) {
  if (raw == null || raw === '') return 0;
  const n = Number(String(raw).replace(',', '.').trim());
  if (Number.isNaN(n) || n < 0) return null;
  return Math.floor(n);
}

/**
 * Descarga plantilla con productos del POS.
 * @param {Array<{sku?:string,name?:string,price?:number}>} products
 * @param {string} [filename]
 */
export async function downloadVentasBulkImportTemplate(products, filename) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Venta masiva');

  const headers = [
    BULK_COL.code,
    BULK_COL.name,
    BULK_COL.price,
    ...QTY_COLS,
  ];

  const headerRow = ws.addRow(headers);
  headerRow.eachCell((cell) => {
    cell.fill = HEADER_FILL;
    cell.font = { bold: true };
  });

  const list = Array.isArray(products) ? products : [];
  list.forEach((p) => {
    ws.addRow([
      p.sku || String(p.id || ''),
      p.name || '',
      p.price != null ? Number(p.price) : '',
      0,
      0,
      0,
      0,
      0,
    ]);
  });

  ws.getColumn(1).width = 18;
  ws.getColumn(2).width = 36;
  ws.getColumn(3).width = 12;
  for (let c = 4; c <= 8; c++) ws.getColumn(c).width = 16;

  const help = wb.addWorksheet('Instrucciones');
  [
    'Importación masiva de ventas (documento Otros, sin IGV).',
    'Una fila = un producto del punto de venta.',
    'Indique cantidades enteras por medio de pago. Cada unidad genera 1 venta.',
    'Medios: cant_efectivo, cant_transferencia, cant_yape, cant_plin, cant_otro.',
    'El precio_pos es informativo; el sistema usa el precio actual del POS.',
    'La fecha y el cliente se eligen en la pantalla de importación.',
    'El stock se descuenta al cerrar el turno del día (1 guía por venta).',
  ].forEach((line, i) => {
    help.getRow(i + 1).getCell(1).value = line;
  });
  help.getColumn(1).width = 90;

  const buf = await wb.xlsx.writeBuffer();
  const safeName = filename || `venta-masiva-${new Date().toISOString().slice(0, 10)}.xlsx`;
  downloadBuffer(buf, safeName);
}

/**
 * Parsea el Excel y devuelve filas listas para el API + resumen.
 * @param {File|ArrayBuffer|Blob} file
 * @param {Array<{id:number,sku?:string,name?:string,price?:number}>} products
 */
export async function parseVentasBulkImportExcel(file, products) {
  const errors = [];
  const catalog = Array.isArray(products) ? products : [];
  const bySku = new Map();
  const byId = new Map();
  catalog.forEach((p) => {
    if (p?.id != null) byId.set(String(p.id), p);
    if (p?.sku) bySku.set(String(p.sku).trim().toLowerCase(), p);
  });

  let buf;
  if (file instanceof ArrayBuffer) {
    buf = file;
  } else if (file?.arrayBuffer) {
    if (file.size > MAX_BULK_FILE_SIZE_BYTES) {
      return { rows: [], errors: ['El archivo supera 5 MB.'], summary: null };
    }
    buf = await file.arrayBuffer();
  } else {
    return { rows: [], errors: ['Archivo inválido.'], summary: null };
  }

  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  const sheet = wb.worksheets[0];
  if (!sheet) {
    return { rows: [], errors: ['El Excel no tiene hojas.'], summary: null };
  }

  const header = findHeaderRow(sheet);
  if (!header) {
    return {
      rows: [],
      errors: [`No se encontró la fila de encabezados (se esperan columnas: ${BULK_COL.code}, ${BULK_COL.cant_efectivo}, …).`],
      summary: null,
    };
  }

  const { rowIndex, colMap } = header;
  const rows = [];
  const byPayment = {};
  VENTAS_PAYMENT_METHODS.forEach((m) => {
    byPayment[m.value] = { count: 0, amount: 0 };
  });
  let totalUnits = 0;
  let totalAmount = 0;

  for (let r = rowIndex + 1; r <= sheet.rowCount; r++) {
    const row = sheet.getRow(r);
    const codeRaw = cellText(row.getCell(colMap[BULK_COL.code]));
    if (!codeRaw) {
      const maybeEmpty = QTY_COLS.every((k) => !cellText(row.getCell(colMap[k] || 0)));
      if (maybeEmpty) continue;
      errors.push(`Fila ${r}: falta codigo.`);
      continue;
    }

    const product =
      bySku.get(codeRaw.toLowerCase())
      || byId.get(codeRaw)
      || null;

    if (!product) {
      errors.push(`Fila ${r}: producto "${codeRaw}" no está en el catálogo del POS (o sin precio).`);
      continue;
    }

    const qtys = {};
    let rowHasQty = false;
    let rowInvalid = false;
    for (const key of QTY_COLS) {
      const col = colMap[key];
      const parsed = parseQty(col ? cellText(row.getCell(col)) : '0');
      if (parsed == null) {
        errors.push(`Fila ${r}: cantidad inválida en ${key}.`);
        rowInvalid = true;
        break;
      }
      qtys[key] = parsed;
      if (parsed > 0) rowHasQty = true;
    }
    if (rowInvalid || !rowHasQty) continue;

    const price = Number(product.price) || 0;
    const methodMap = {
      cant_efectivo: 'efectivo',
      cant_transferencia: 'transferencia',
      cant_yape: 'yape',
      cant_plin: 'plin',
      cant_otro: 'otro',
    };
    Object.entries(methodMap).forEach(([col, method]) => {
      const q = qtys[col] || 0;
      if (q <= 0) return;
      byPayment[method].count += q;
      byPayment[method].amount = Math.round((byPayment[method].amount + q * price) * 100) / 100;
      totalUnits += q;
      totalAmount = Math.round((totalAmount + q * price) * 100) / 100;
    });

    rows.push({
      product_id: product.id,
      code: product.sku || String(product.id),
      ...qtys,
    });
  }

  if (rows.length === 0 && errors.length === 0) {
    errors.push('No se importó ninguna fila con cantidades.');
  }

  return {
    rows,
    errors,
    summary: {
      product_rows: rows.length,
      total_units: totalUnits,
      total_amount: totalAmount,
      by_payment: byPayment,
    },
  };
}
